import * as React from "react";
import { useEffect } from "react";
import { connectPageService } from "@graffer-inc/smart-apply";
import { ApplicationsService } from "../../../../application/service/smart-apply/applications/applications-service";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { useLocation } from "react-router-dom";
import { ApplicationList } from "../../../organisms/smart-apply/application-list";
import { ApplicationsContextProps } from "../../../../application/service/smart-apply/applications/applications-service";
import { DownloadCsvModal } from "../../../organisms/smart-apply/modals/download-csv-modal";
import { useModal } from "../../../../utils/custom-hooks";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { BatchStatusUpdateModal } from "../../../organisms/smart-apply/modals/batch-status-update-modal";
import { CsvUploadModal } from "../../../organisms/common/csv-upload-modal";
import { BulkIssuePdfFileModal } from "../../../organisms/common/bulk-issue-pdf-file-modal";
import { BulkUpdateTenantProcessResultModal } from "../../../organisms/common/bulk-update-tenant-process-result-modal";
import { BulkDownloadFilesModalContainer } from "../../../organisms/smart-apply/modals/bulk-download-files";
import {
  InformationBanner,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { Pagination } from "@graffer-inc/console-design-kit";
import { SmartApplyMenuList } from "../../../organisms/smart-apply/menu-list";
import { ApplicationsHeader } from "./contents-header";
import classnames from "classnames";
import "./applications.scss";
import { matchType } from "../../../../application/domain/base/base-type";

export const Appls: React.FC<ApplicationsContextProps> = ({
  isLoading,
  applications,
  selected,
  paging,
  sort,
  setSort,
  setPage,
  movePage,
  setDateRange,
  selectedStatus,
  setSelectedStatus,
  setSelectedProcedureId,
  selectedProcedure,
  selectAll,
  searchApplications,
  selectApplicationToggle,
  fetchApplications,
  selectedMetaIds,
  toggleAllCsvMeta,
  toggleCsvMeta,
  downloadCSV,
  selectByCSV,
  batchUpdateStatusResult,
  clearBatchUpdateStatusResult,
  batchUpdateStatus,
  downloadSamplePDF,
  batchIssueFiles,
  batchUpdateTenantProcessResult,
  isDownloadingSamplePDF,
}) => {
  const {
    page: p,
    procedureId,
    from,
    to,
    sort: parsedSort,
    status,
  } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    procedureId: string | undefined;
    from: string | undefined;
    to: string | undefined;
    sort: string | undefined;
    status: string | undefined;
  };
  const downloadCsvModal = useModal();
  const statusUpdateModal = useModal();
  const csvUploadModal = useModal();
  const pdfGenerateModal = useModal();
  const tenantProcessResultUpdateModal = useModal();
  const bulkDownloadFilesModal = useModal();

  const [dateFrom, dateTo] = React.useMemo(() => {
    const parsedFrom = Date.parse(from!);
    const parsedTo = Date.parse(to!);
    return [
      isNaN(parsedFrom) ? undefined : new Date(parsedFrom),
      isNaN(parsedTo) ? undefined : new Date(parsedTo),
    ];
  }, [from, to]);

  useEffect(() => {
    setPage(parseInt(p || "1", 10));
    const parsedStatus = status ? JSON.parse(status) : undefined;
    setDateRange({
      from: dateFrom,
      to: dateTo,
    });
    if (parsedStatus) setSelectedStatus(parsedStatus);
    setSort(parsedSort);
    setSelectedProcedureId(procedureId);
    fetchApplications();
  }, [
    p,
    setPage,
    from,
    to,
    dateFrom,
    dateTo,
    setDateRange,
    setSort,
    fetchApplications,
    procedureId,
    parsedSort,
    setSelectedProcedureId,
    status,
    setSelectedStatus,
  ]);
  const isCsvSelected = !!procedureId && !!selected.csv;
  const inputElements =
    selectedProcedure?.published?.template.tenantProcessResultSetting
      ?.inputElements;
  const selectedApplications = React.useMemo(
    () =>
      applications.filter((a) =>
        selected.applicationIds.includes(a.application.id),
      ),
    [applications, selected.applicationIds],
  );

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "申請一覧" }]}
      menuList={<SmartApplyMenuList />}
      contentsHeader={
        <ApplicationsHeader
          setDateRange={setDateRange}
          setSort={setSort}
          sort={sort}
          setSelectedProcedureId={setSelectedProcedureId}
          selectedProcedure={selectedProcedure}
          dateFrom={dateFrom}
          dateTo={dateTo}
          searchApplications={searchApplications}
          downloadCsvModal={downloadCsvModal}
          tenantProcessResultUpdateModal={tenantProcessResultUpdateModal}
          procedureId={procedureId}
          inputElements={inputElements}
          selectedIds={selected.applicationIds}
        />
      }
    >
      {isCsvSelected && (
        <div className="notification p-6" style={{ marginRight: 36 }}>
          <button className="delete" onClick={() => selectByCSV(undefined)} />
          <p className="title is-5">
            以下のCSVファイルに含まれる申請IDが操作対象として選択されています。
          </p>
          <p className="subtitle">{selected.csv!.name}</p>
          <div className="buttons">
            <SecondaryButton
              text="対応ステータスを更新"
              onClick={statusUpdateModal.open}
              data-for-rpa-id={"update-status"}
            />
            <SecondaryButton
              text="ファイルを交付"
              onClick={pdfGenerateModal.open}
              disabled={!selectedProcedure?.canOutputPdf()}
            />
          </div>
        </div>
      )}
      {!isCsvSelected && (
        <div>
          <ApplicationList
            isLoading={isLoading}
            selectable={{
              selectAll: selectAll,
              selected: selectApplicationToggle,
              selectedProcedure: selectedProcedure,
              selectedApplicationIds: selected.applicationIds,
            }}
            appls={applications}
            setSelectedStatus={setSelectedStatus}
            selectedStatus={selectedStatus}
            paging={paging}
            openStatusUpdateModal={statusUpdateModal.open}
            openBulkIssuePDFModal={pdfGenerateModal.open}
            openCsvUploadModal={csvUploadModal.open}
            openCsvDownloadModal={downloadCsvModal.open}
            openBulkDownloadFilesModal={bulkDownloadFilesModal.open}
          />
          {!isLoading &&
            applications.length === 0 &&
            matchType(
              !procedureId && !from && !to ? "no_registered" : "no_results",
              {
                no_registered: (
                  <div className="py-2">
                    <InformationBanner
                      message="申請がまだ登録されていません。"
                      description="初めての申請が登録されると、ここに表示されます。"
                    />
                  </div>
                ),
                no_results: (
                  <div className="py-2">
                    <InformationBanner
                      message="条件に該当する申請はありませんでした。"
                      description="絞り込み条件を確認してください。"
                    />
                  </div>
                ),
              },
            )}

          {paging.total > 0 && (
            <div className={classnames("applications-pagination-footer")}>
              <Pagination
                onPageClick={movePage}
                currentPage={paging.current}
                lastPage={paging.total}
              />
            </div>
          )}
        </div>
      )}
      {selectedProcedure && (
        <DownloadCsvModal
          isActive={downloadCsvModal.isActive}
          page={paging.current}
          selectedProcedure={selectedProcedure}
          selectable={{
            selectedMetaIds: selectedMetaIds,
            toggleById: toggleCsvMeta,
            toggleAll: toggleAllCsvMeta,
          }}
          onDownloadCsv={downloadCSV}
          onClose={downloadCsvModal.close}
        />
      )}
      {statusUpdateModal.isActive && (
        <BatchStatusUpdateModal
          isActive
          onUpdateStatus={batchUpdateStatus}
          onClose={() => {
            statusUpdateModal.close();
            clearBatchUpdateStatusResult();
          }}
          result={batchUpdateStatusResult}
        />
      )}
      {csvUploadModal.isActive && (
        <CsvUploadModal
          isActive
          procedureName={selectedProcedure?.name ?? ""}
          onClick={(file) => {
            selectByCSV(file);
            csvUploadModal.close();
          }}
          onClose={csvUploadModal.close}
        />
      )}
      {pdfGenerateModal.isActive && selectedProcedure && (
        <BulkIssuePdfFileModal
          isActive
          isDownloadingSamplePDF={isDownloadingSamplePDF}
          procedure={selectedProcedure}
          selectedCondition={selected}
          onExecute={batchIssueFiles}
          onPreview={downloadSamplePDF}
          onClose={pdfGenerateModal.close}
        />
      )}
      {!!inputElements && (
        <BulkUpdateTenantProcessResultModal
          isActive={tenantProcessResultUpdateModal.isActive}
          onClose={tenantProcessResultUpdateModal.close}
          onExecute={batchUpdateTenantProcessResult}
          inputElements={inputElements}
        />
      )}
      {bulkDownloadFilesModal.isActive && selectedProcedure && (
        <BulkDownloadFilesModalContainer
          procedure={selectedProcedure}
          applications={selectedApplications.map(
            ({ application }) => application,
          )}
          onClose={bulkDownloadFilesModal.close}
        />
      )}
    </SmartApplyTemplate>
  );
};

export const ApplsContainer = connectPageService(
  Appls,
  () => new ApplicationsService(),
);
